import { NavLink, useNavigate, useMatch, useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard, Folder, ClipboardList, Boxes, FileImage, AlertTriangle,
  Activity, LogOut, HardHat, Menu, X, Mail, Map, Building2, Users as UsersIcon, MapPin, ShieldCheck, Settings as SettingsIcon, UserCog,
  Plus, Send, Upload, ChevronLeft,
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

// ------- Nav definitions -----------------------------------------------------
const WORKSPACE_NAV = [
  { to: "/projects", label: "Projects", icon: Folder, testid: "nav-projects" },
  { to: "/users", label: "Users", icon: UsersIcon, testid: "nav-users", roles: ["admin", "qa_manager"] },
  { to: "/settings/organisation", label: "Organisation", icon: Building2, testid: "nav-org-settings", roles: ["admin"] },
  { to: "/settings/integrations", label: "Settings", icon: SettingsIcon, testid: "nav-settings", roles: ["admin"] },
];

function projectNav(pid) {
  return [
    { to: `/p/${pid}/dashboard`, label: "Dashboard", icon: LayoutDashboard, testid: "nav-project-dashboard" },
    { to: `/p/${pid}/itp`, label: "ITP Templates", icon: ClipboardList, testid: "nav-project-itp" },
    { to: `/p/${pid}/lots`, label: "Lots", icon: Boxes, testid: "nav-project-lots" },
    { to: `/p/${pid}/plans`, label: "Plans & Drawings", icon: Map, testid: "nav-project-plans" },
    { to: `/p/${pid}/lot-maps`, label: "Lot Maps", icon: MapPin, testid: "nav-project-lot-maps" },
    { to: `/p/${pid}/evidence`, label: "Evidence", icon: FileImage, testid: "nav-project-evidence" },
    { to: `/p/${pid}/ncrs`, label: "NCRs", icon: AlertTriangle, testid: "nav-project-ncrs" },
    { to: `/p/${pid}/audit`, label: "Audit Trail", icon: Activity, testid: "nav-project-audit" },
    { to: `/p/${pid}/emails`, label: "Email Outbox", icon: Mail, testid: "nav-project-emails" },
  ];
}

const PLATFORM_NAV = [
  { to: "/platform/companies", label: "Companies", icon: Building2, testid: "nav-platform-companies" },
  { to: "/platform/access-requests", label: "Access Requests", icon: Mail, testid: "nav-platform-access-requests" },
  { to: "/platform/settings", label: "Settings", icon: SettingsIcon, testid: "nav-platform-settings" },
];

function Sidebar({ onClose, user, onLogout }) {
  const isPlatform = user?.role === "platform_admin";
  const projectMatch = useMatch("/p/:projectId/*");
  const inProject = !!projectMatch;
  const projectId = projectMatch?.params?.projectId;
  let navItems;
  let mode;
  if (isPlatform) {
    navItems = PLATFORM_NAV;
    mode = "platform";
  } else if (inProject) {
    navItems = projectNav(projectId);
    mode = "project";
  } else {
    navItems = WORKSPACE_NAV.filter((n) => !n.roles || n.roles.includes(user?.role));
    mode = "workspace";
  }
  return (
    <div className="flex h-full w-64 flex-col border-r border-slate-200 bg-white">
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-5 py-4">
        <div className="flex items-center gap-2">
          <img src="/brand/flowqa-icon.png" alt="FlowQA" className="size-9 rounded-md bg-slate-900 object-contain p-1" data-testid="sidebar-logo" />
          <div>
            <div className="font-heading text-lg font-black tracking-tighter text-slate-900">
              FlowQA
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
              {mode === "platform" ? "Owner Console" : mode === "project" ? "Project" : "Workspace"}
            </div>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="lg:hidden" data-testid="sidebar-close">
            <X className="size-5 text-slate-500" />
          </button>
        )}
      </div>
      {mode === "project" && (
        <NavLink to="/projects" onClick={onClose} data-testid="sidebar-back-to-projects"
                  className="border-b border-slate-100 bg-teal-50/60 px-5 py-2.5 text-xs font-semibold text-teal-800 hover:bg-teal-100 flex items-center gap-1.5">
          <ChevronLeft className="size-3.5" /> All Projects
        </NavLink>
      )}
      {isPlatform && (
        <div className="border-b border-slate-100 bg-teal-50/60 px-5 py-2.5">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-teal-800">
            <ShieldCheck className="size-3.5" /> FlowQA Owner
          </div>
        </div>
      )}
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {navItems.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/" || n.to === "/platform/companies"}
            data-testid={n.testid}
            onClick={onClose}
            className={({ isActive }) =>
              cn(
                "group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? (n.to.endsWith("/dashboard")
                      ? "bg-orange-500 text-white shadow-sm hover:bg-orange-600"
                      : "bg-sky-50 text-teal-800")
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              )
            }
          >
            <n.icon className="size-4" />
            {n.label}
          </NavLink>
        ))}
        {mode === "project" && (
          <div className="mt-4 border-t border-slate-100 pt-3">
            <div className="mb-1 px-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">Shortcuts</div>
            <NavLink to={`/p/${projectId}/ncrs?create=1`} onClick={onClose} data-testid="shortcut-create-ncr" className="group flex items-center gap-3 rounded-md px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900">
              <Plus className="size-3.5" /> Create NCR
            </NavLink>
            <NavLink to={`/p/${projectId}/lots`} onClick={onClose} data-testid="shortcut-request-release" className="group flex items-center gap-3 rounded-md px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900">
              <Send className="size-3.5" /> Request Release
            </NavLink>
            <NavLink to={`/p/${projectId}/evidence`} onClick={onClose} data-testid="shortcut-upload-evidence" className="group flex items-center gap-3 rounded-md px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900">
              <Upload className="size-3.5" /> Upload Evidence
            </NavLink>
          </div>
        )}
      </nav>
      <div className="border-t border-slate-200 px-4 py-3">
        <div className="mb-2">
          <div className="text-sm font-semibold text-slate-900" data-testid="user-name">
            {user?.name}
          </div>
          <div className="text-xs uppercase tracking-wider text-slate-500" data-testid="user-role">
            {user?.role === "platform_admin" ? "FlowQA Owner" : user?.role?.replace("_", " ")}
          </div>
        </div>
        <NavLink
          to="/account/settings"
          onClick={onClose}
          data-testid="nav-account-settings"
          className={({ isActive }) =>
            cn(
              "mb-2 flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
              isActive
                ? "bg-sky-50 text-teal-800"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            )
          }
        >
          <UserCog className="size-3.5" /> Account settings
        </NavLink>
        <Button
          data-testid="logout-btn"
          variant="outline"
          className="w-full justify-start"
          onClick={onLogout}
        >
          <LogOut className="mr-2 size-4" /> Sign out
        </Button>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const onLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="flex h-screen bg-slate-50">
      <div className="hidden lg:block">
        <Sidebar user={user} onLogout={onLogout} />
      </div>
      {open && (
        <div className="fixed inset-0 z-40 flex lg:hidden">
          <div className="absolute inset-0 bg-slate-900/60" onClick={() => setOpen(false)} />
          <div className="relative z-50">
            <Sidebar onClose={() => setOpen(false)} user={user} onLogout={onLogout} />
          </div>
        </div>
      )}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 lg:hidden">
          <button data-testid="sidebar-open" onClick={() => setOpen(true)}>
            <Menu className="size-6 text-slate-700" />
          </button>
          <div className="flex items-center gap-2">
            <img src="/brand/flowqa-icon.png" alt="FlowQA" className="size-7 rounded-md bg-slate-900 object-contain p-0.5" />
            <span className="font-heading text-base font-black tracking-tighter">FlowQA</span>
          </div>
          <div className="text-xs text-slate-500">{user?.role?.replace("_", " ")}</div>
        </header>
        <main className="flex-1 overflow-y-auto" data-testid="main-content">{children}</main>
      </div>
    </div>
  );
}
